import React, {useEffect, useMemo, useState} from 'react';
import {HStack, Pressable, VStack} from "native-base";
import {HOK, IMMEDIATE, RELATIVE, TRIAL} from "../../../../../../services/ConstHelper";
import FoldableSettingSection from "../../../../../common/FoldableSettingSection";
import {t} from "../../../../../../services/i18n";
import AppText from "../../../../../common/AppText";
import {GlobalStyleAttributes} from "../../../../../../styles/GlobalStyles";
import MultiSingleMenuWithSearch from "../../../../../common/MultiSingleMenuWithSearch";
import FormInput from "../../../../../common/FormInput";
import {Radio} from "antd";
import {Colors} from "../../../../../../styles/Colors";
import uniq from 'lodash-es/uniq';
import {useSelector} from "react-redux";
import usePermissions from "../../../../../../services/Permissions";
import {fetchApi} from "../../../../../../services/HTTP";
import {isBoxIL} from "../../../../../../services/Helpers";
import InfoTooltip from "../../../../../common/InfoTooltip";
import AdvanceSelect from "../../../../../common/AdvanceSelect";
import SimpleInput from "../../../../../common/SimpleInput";
import {checkRequired} from "../../../../../../services/stepperService";
import {store} from "../../../../../../redux/store";

const TRIAL_SECTION = 'TRIAL_SECTION'
const ADDONS_SECTION = 'ADDONS_SECTION'
const HUG_FORM_SECTION = 'HUG_FORM_SECTION'

//THIS STEP IS HIGHLY DEPENDENT ON "SERIES_PAYMENT_OPTIONS" STEP!!! THEY MUST GO TOGETHER (THIS STEP IS "COSMETIC" SEPARATION BECAUSE IT WAS GETTING TOO LONG)
const AdvancedSeriesSettings = (props) => {
    const { values, errors, touched, setFieldValue, isEdit, showTrialOption, showAddonsOption, showHugFormOption } = props;
    const box = useSelector((state) => state.box.box);

    const [openedSection, setOpenedSection] = useState(null);
    const [addonOptions, setAddonOptions] = useState(null);
    const [isReady, setIsReady] = useState(false);
    const [formOptions, setFormOptions] = useState([]);
    const { hasLock, has } = usePermissions();

    useEffect(() => {
        // console.log('values, errors', values, errors)
    }, [values, errors]);

    const seriesEvents = useMemo(() => isEdit ? values.days : values.multiDayConfig?.days, [isEdit, values.multiDayConfig?.days]);
    const hasHOKOption = useMemo(() => has('recurringPaymentActions'), []);
    const selectedLocation = useMemo(() => {
        if(box && values.locations_box_fk) {
            return box.locations_box.find(l => l.id === values.locations_box_fk)
        }
    },[box, values.locations_box_fk])
    const currencySymbol = useMemo(() => selectedLocation?.currency_symbol,[selectedLocation])
    const allowOnlyDebt = useMemo(() => selectedLocation?.hasPayments === false, [selectedLocation]);
    const hasImmediate = useMemo(() => !values.isUnlimited, [values.isUnlimited]);
    const isGlobal = useMemo(() => !isBoxIL(), [box]);
    const taxes = useMemo(() => {
        if (selectedLocation && selectedLocation.taxes.length > 0) {
            return selectedLocation.taxes.reduce((acc, tax) => [...acc, {id: tax.id, name: `${tax.name} (${tax.percentage}%)`}],[])
        }
    },[selectedLocation])

    useEffect(() => {
        getDigitalForms()
    }, []);

    useEffect(() => {
        if(values.locations_box_fk) {
            fetchAddonOptions(values.locations_box_fk)
        }
    }, [values.locations_box_fk]);

    useEffect(() => {
        if(!isReady && (values.partialOptions.length === 0 || isEdit)) {
            setIsReady(true)
            setTimeout(() => initPartialOptions(), 100)
        } else if(!isReady && values.partialOptions.length > 0) { //not edit, user changed his payment option after this step has been configured already
            setIsReady(true)
            setTimeout(() => checkFullPaymentOrDaysHasChanged(), 100)
        }
    }, [hasHOKOption, hasImmediate, isReady, values.partialOptions]);

    const getDigitalForms = async () => {
        const res = await fetchApi('digitalForm/getAllForms', 'GET', null, false, true);
        if(res && res.length) {
            setFormOptions(res.map(df => ({...df, value: df.id, label: df.name})))
        }
    }

    const initPartialOptions = () => {
        if(seriesEvents?.length > 1) {
            let {partialOptions, update} = addPartialOptions(values.partialOptions ?? [])
            if(isEdit) { //handle full payment has changed in edit mode
                const res = handleSiteDisplayUpdates(partialOptions)
                partialOptions = res.partialOptions
                update = (update || res.update)
            }
            if(update) {
                setFieldValue('partialOptions', partialOptions)
            }
        }
    }

    const checkFullPaymentOrDaysHasChanged = () => {
        let partialOptions = values.partialOptions ?? []
        // partialOptions = partialOptions.map(option => ({...option, show_in_app: 0})) //reset all payment options
        const res = handleSiteDisplayUpdates(partialOptions)
        partialOptions = res.partialOptions

        //if number of days in the course was for example 3 and then the user went back and removed 1 day, remove membership types with daysOfWeek=2 (because there is no option to book partially for 2 days, it's the entire course)
        partialOptions = partialOptions.filter(option => option.daysOfWeek < seriesEvents.length)

        //if number of days in the course was for example 2 and then the user went back and add 2 more days (so total of 4 days now), add new membership types with daysOfWeek=2 and daysOfWeek=3
        const biggestPartialOption = partialOptions.find(option => option.daysOfWeek === seriesEvents.length - 1)
        if(!biggestPartialOption) {
            const res = addPartialOptions(partialOptions)
            partialOptions = res.partialOptions
        }
        setFieldValue('partialOptions', partialOptions)
    }

    const addPartialOptions = (partialOptions = []) => {
        let indexHOK = findPaymentTypeIndex(HOK)
        let indexImmediate = findPaymentTypeIndex(IMMEDIATE)
        let update = false;
        for (let i = 1; i < seriesEvents.length ; i++) {
            let name = `${values.multiDayConfig?.seriesName ?? values.series_name} - ${t('times-a-week', {num: i})}`
            if(hasHOKOption && indexHOK >= 0) {
                const partialObj = values.partialOptions.find(payment => payment.type === HOK && payment.daysOfWeek === i)
                if(!partialObj) {
                    const hokMembership = values.paymentOptions[indexHOK]
                    if (hokMembership.id) delete hokMembership.id
                    partialOptions.push({...hokMembership, daysOfWeek: i, price: 1, show_in_app: 0, name: name})
                    update = true
                } else if(partialObj.relative_payment !== values.paymentOptions[indexHOK].relative_payment) {
                    partialOptions = partialOptions.map(payment => (payment.type === HOK && payment.daysOfWeek === i) ? ({...payment, relative_payment: values.paymentOptions[indexHOK].relative_payment}) : payment)
                    update = true
                }
            }
            if(hasImmediate && indexImmediate >= 0) {
                const partialObj = values.partialOptions.find(payment => payment.type === IMMEDIATE && payment.daysOfWeek === i)
                if(!partialObj) {
                    const immediateMembership = values.paymentOptions[indexImmediate]
                    if (immediateMembership.id) delete immediateMembership.id
                    partialOptions.push({...immediateMembership, daysOfWeek: i, price: 0, show_in_app: 0, name: name})
                    update = true
                } else if(partialObj.relative_payment !== values.paymentOptions[indexImmediate].relative_payment) {
                    partialOptions = partialOptions.map(payment => (payment.type === IMMEDIATE && payment.daysOfWeek === i) ? ({...payment, relative_payment: values.paymentOptions[indexImmediate].relative_payment}) : payment)
                    update = true
                }
            }
        }
        return {partialOptions, update}
    }

    const handleSiteDisplayUpdates = (partialOptions) => {
        let update = false;
        const fullPaymentOption = values.paymentOptions.find(opt => opt.type !== TRIAL && opt.show_in_app > 0)
        if(fullPaymentOption) { //if one of payment options are open for purchase, reset only the ones that don't have the same payment type and has show_in_app>0
            partialOptions = partialOptions.map(opt => {
                if(opt.type !== fullPaymentOption.type && opt.show_in_app > 0) {
                    opt.show_in_app = 0
                    update = true
                }
                return opt
            })
        } else { //if all options are show_in_app=0, reset all partial as well
            partialOptions = partialOptions.map(opt => {
                if(opt.show_in_app !== 0) {
                    opt.show_in_app = 0
                    update = true
                }
                return opt
            })
        }
        return {partialOptions, update}
    }

    const fetchAddonOptions = async (locationId) => {
        try {
            const res = await fetchApi('getAddonOptions', 'POST' , { locationId, type: 'hugim' },false, true);
            let options = res.map(item => ({...item, label: `${item.name} (${item.price.toLocaleString()}${currencySymbol})`}))
            setAddonOptions(options);
        } catch (e) {
            console.log('error fetching addons')
        }
    }

    const onSectionToggle = (sectionName) => {
        if(openedSection === sectionName) setOpenedSection(null)
        else setOpenedSection(sectionName)
    }

    const onTrialSelectChange = (selection) => {
        const trialIndex = findPaymentTypeIndex(TRIAL)
        if(selection === 'no-trial') {
            setFieldValue(`paymentOptions[${trialIndex}].show_in_app`, 0)
            setFieldValue(`paymentOptions[${trialIndex}].price`, 0)
        } else {
            setFieldValue(`paymentOptions[${trialIndex}].show_in_app`, 1)
            setFieldValue(`paymentOptions[${trialIndex}].price`, 0)
        }
    }

    const getPaymentError = (paymentType, propName) => {
        const typeIndex = findPaymentTypeIndex(paymentType)
        if(typeIndex !== -1 && errors.paymentOptions && touched.paymentOptions && errors.paymentOptions[typeIndex] && touched.paymentOptions[typeIndex]) {
            return errors.paymentOptions[typeIndex][propName]
        }
    }

    const getPartialError = (typeIndex, propName) => {
        if(typeIndex !== -1 && errors.partialOptions && touched.partialOptions && errors.partialOptions[typeIndex] && touched.partialOptions[typeIndex]) {
            return errors.partialOptions[typeIndex][propName]
        }
    }

    const getAddonsSection = () => {
        const selected = uniq(values.paymentOptions?.reduce((acc, opt) => [...acc, ...(opt.addons ?? [])],[]) ?? [])
        const onSelection = (vals) => {
            const hokIndex = findPaymentTypeIndex(HOK)
            const immediateIndex = findPaymentTypeIndex(IMMEDIATE)
            if(hokIndex >= 0) setFieldValue(`paymentOptions[${hokIndex}].addons`, vals)
            if(immediateIndex >= 0) setFieldValue(`paymentOptions[${immediateIndex}].addons`, vals)
            const partialOptions = [...values.partialOptions];
            partialOptions.forEach((option, i) => {
                setFieldValue(`partialOptions[${i}].addons`, vals)
            })
        }
        return (
            <FoldableSettingSection header={t('addons-hugim-selection-header')}
                                    infoTooltip={t('addons-hugim-selection-tooltip')}
                                    isOpen={openedSection === ADDONS_SECTION}
                                    onToggle={() => onSectionToggle(ADDONS_SECTION)}
                                    summary={t(`hug-addons-summary${selected.length === 0 ? '-empty' : ''}`)}
                                    hasLock={hasLock('hugimAddons')}
            >
                <VStack space={'0.2rem'} flex={1}>
                    <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('products')}</AppText>
                    <MultiSingleMenuWithSearch options={addonOptions}
                                               labelProp={'label'}
                                               onChange={onSelection}
                                               placeholder={t('product-addons-select-placeholder')}
                                               defaultValue={selected}
                    />
                </VStack>
            </FoldableSettingSection>)
    }

    const renderTrialSection = () => {
        const trialIndex = findPaymentTypeIndex(TRIAL)
        if(trialIndex < 0) return null

        const selectedOption = values?.paymentOptions?.[trialIndex].show_in_app === 0 ? 'no-trial' : 'allow-trial-class'

        return (
            <FoldableSettingSection header={t('trial-hugim-selection-header')}
                                    infoTooltip={t(`${isEdit ? 'edit-' : ''}trial-hugim-selection-tooltip`)}
                                    isOpen={openedSection === TRIAL_SECTION}
                                    onToggle={() => onSectionToggle(TRIAL_SECTION)}
                                    summary={t(`hug-trial-summary-${selectedOption}`,{price: `${currencySymbol}${(values?.paymentOptions?.[trialIndex]?.price || 0).toLocaleString()}`})}
                                    errorIcon={getPaymentError(TRIAL, 'price')}
            >
                <VStack space={'0.5rem'}>
                    <VStack>
                        <FormInput
                            value={values?.paymentOptions?.[trialIndex]?.price}
                            onChangeCallback={(val) => setFieldValue(`paymentOptions[${trialIndex}].price`, val)}
                            label={t('price')}
                            width={'6rem'}
                            formatAddon={currencySymbol}
                            inputType={'float'}
                            error={getPaymentError(TRIAL, 'price')}
                            containerProps={{mb: 0}}
                            errorHeight={'unset'}
                        />
                        {taxes &&
                            <VStack space={'0.2rem'} w={'12rem'}>
                                <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('taxes')}</AppText>
                                <MultiSingleMenuWithSearch options={taxes} onChange={(val) => setFieldValue(`paymentOptions[${trialIndex}].taxes`, val)} placeholder={t('taxSelectorLabel')} defaultValue={values?.paymentOptions?.[trialIndex]?.taxes}/>
                            </VStack>}
                    </VStack>
                    <VStack space={'0.2rem'} flex={1}>
                        <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('display-on')}</AppText>
                        <Radio.Group name={'trial-hugim-radio'} onChange={e => onTrialSelectChange(e.target.value)} value={selectedOption}>
                            <VStack space={'0.5rem'}>
                                <Radio value="no-trial" size="sm"><AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('no-trial-hugim-site')}</AppText></Radio>
                                <Radio value="allow-trial-class" size="sm">
                                    <HStack><AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t(`${allowOnlyDebt ? 'debt-' : ''}allow-trial-hugim-site`)}</AppText></HStack>
                                </Radio>
                            </VStack>
                        </Radio.Group>
                    </VStack>
                </VStack>
            </FoldableSettingSection>
        )
    }

    const findPaymentTypeIndex = (type) => {
        return values.paymentOptions.findIndex(option => option.type === type)
    }
    const findPartialTypeIndex = (type, daysOfWeek) => {
        return values.partialOptions.findIndex(option => option.type === type && option.daysOfWeek === daysOfWeek)
    }

    const changeDisplayOnSite = (selection, daysOfWeek) => {
        let indexHOK = findPaymentTypeIndex(HOK)
        let indexImmediate = findPaymentTypeIndex(IMMEDIATE)
        let indexPartialHOK = findPartialTypeIndex(HOK, daysOfWeek)
        let indexPartialImmediate = findPartialTypeIndex(IMMEDIATE, daysOfWeek)
        let valueHOK = 0
        let valueImmediate = 0

        if(selection !== 'hide') {
            if (indexHOK >= 0 && values.paymentOptions[indexHOK].show_in_app === 1) {
                valueHOK = 1
                valueImmediate = 0
            } else if (indexImmediate >= 0 && values.paymentOptions[indexImmediate].show_in_app > 0) {
                valueHOK = 0
                valueImmediate = values.paymentOptions[indexImmediate].show_in_app
            }
        }
        if(indexPartialHOK >= 0) {
            setFieldValue(`partialOptions[${indexPartialHOK}].show_in_app`, valueHOK)
        }
        if(indexPartialImmediate >= 0) {
            setFieldValue(`partialOptions[${indexPartialImmediate}].show_in_app`, valueImmediate)
        }
    }

    const renderPartialSection = (daysOfWeek) => {
        let indexHOK = findPartialTypeIndex(HOK, daysOfWeek)
        let indexImmediate = findPartialTypeIndex(IMMEDIATE, daysOfWeek)
        const options = values.partialOptions.filter(payment => payment.daysOfWeek === daysOfWeek)
        if(options.length === 0) return null
        const displayOnSite = options.some(payment => payment.show_in_app > 0)
        const disableSiteRadio = values.paymentOptions.every(payment => payment.show_in_app === 0)
        const name = options[0].name

        const getSummary = () => {
            let summary = '';
            if(!displayOnSite) {
                return t('partial-registration-summary-hide', {num: daysOfWeek})
            }
            let paymentOptionsText = '';
            options.map((option, i) => {
                paymentOptionsText += t(`series-payments-price-${option.type}-summary`, {price: `${(option.price || 0).toLocaleString()}${currencySymbol}`, max_installments: option.max_installments})
                if(option.type === HOK) {
                    paymentOptionsText += ` ${t(`series-HOK-summary-${option.relative_payment === RELATIVE ? 'relative' : 'full'}${values.isUnlimited ? '-unlimited' : ''}`)}`
                } else if(option.type === IMMEDIATE) {
                    paymentOptionsText += ` ${t(`series-immediate-summary-${option.relative_payment === RELATIVE ? 'relative' : 'full'}`)}`
                }
                if(i < options.length - 1) {
                    paymentOptionsText += ` ${t('and')} `
                }
            })
            summary = t('partial-registration-summary', {num: daysOfWeek, paymentOptions: paymentOptionsText})
            return summary
        }

        const setName = (newName) => {
            if(indexHOK >= 0) {
                setFieldValue(`partialOptions[${indexHOK}].name`, newName)
            }
            if(indexImmediate >= 0) {
                setFieldValue(`partialOptions[${indexImmediate}].name`, newName)
            }
        }

        return (
            <FoldableSettingSection header={t(`partial-section-header-${daysOfWeek}-day`)}
                                    isOpen={openedSection === `PARTIAL_${daysOfWeek}_SECTION`}
                                    onToggle={() => onSectionToggle(`PARTIAL_${daysOfWeek}_SECTION`)}
                                    summary={getSummary()}
                                    key={`partial-section-${daysOfWeek}`}
            >
                <VStack space={'1rem'}>
                    <VStack space={'1rem'}>
                        <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('partial-registration-display')}</AppText>
                        <Radio.Group disabled={disableSiteRadio} name={`display-partial-${daysOfWeek}-radio`} value={displayOnSite ? 'show-site' : 'hide'} onChange={e => changeDisplayOnSite(e.target.value, daysOfWeek)}>
                            <VStack space={'0.8rem'}>
                                <Radio value={'hide'} size="sm"><AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('hidePartialOnSite-radio')}</AppText></Radio>
                                <Radio value={'show-site'} size="sm"><AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('showPartialOnSite-radio')}</AppText></Radio>
                            </VStack>
                        </Radio.Group>
                    </VStack>
                    {
                        indexHOK >= 0 &&
                        <VStack space={'0.5rem'}>
                            <HStack>
                                <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('recurring-payment-settings')}</AppText>
                                <InfoTooltip title={t('recurring-payment-tooltip')}/>
                            </HStack>
                            <VStack space={'0.5rem'}>
                                <FormInput
                                    value={values.partialOptions[indexHOK].price}
                                    onChangeCallback={(val) => setFieldValue(`partialOptions[${indexHOK}].price`, val)}
                                    label={t('price-per-month')}
                                    width={'6rem'}
                                    formatAddon={currencySymbol}
                                    inputType={'float'}
                                    error={getPartialError(indexHOK, 'price')}
                                    containerProps={{mb: 0}}
                                    errorHeight={'unset'}
                                    labelProps={{style:{fontFamily: 'Ploni'}}}
                                />
                                {taxes &&
                                    <VStack space={'0.2rem'} w={'12rem'}>
                                        <AppText fontSize={GlobalStyleAttributes.fonts.infoText}>{t('taxes')}</AppText>
                                        <MultiSingleMenuWithSearch options={taxes} onChange={(val) => setFieldValue(`partialOptions[${indexHOK}].taxes`, val)} placeholder={t('taxSelectorLabel')} defaultValue={values.partialOptions[indexHOK].taxes}/>
                                    </VStack>
                                }
                            </VStack>
                        </VStack>
                    }
                    {
                        indexImmediate >= 0 &&
                        <VStack space={'0.5rem'}>
                            <HStack>
                                <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('immediate-payment-settings')}</AppText>
                                <InfoTooltip title={t('immediate-payment-tooltip')}/>
                            </HStack>
                            <VStack space={'0.5rem'}>
                                <HStack space={'0.5rem'}>
                                    <FormInput
                                        value={values.partialOptions[indexImmediate].price}
                                        onChangeCallback={(val) => setFieldValue(`partialOptions[${indexImmediate}].price`, val)}
                                        label={t('total-price')}
                                        width={'6rem'}
                                        formatAddon={currencySymbol}
                                        inputType={'float'}
                                        error={getPartialError(indexImmediate, 'price')}
                                        containerProps={{mb: 0}}
                                        errorHeight={'unset'}
                                        labelProps={{style:{fontFamily: 'Ploni'}}}
                                    />
                                    {!allowOnlyDebt && !isGlobal &&
                                        <FormInput
                                            value={values.partialOptions[indexImmediate].max_installments}
                                            onChangeCallback={(val) => setFieldValue(`partialOptions[${indexImmediate}].max_installments`, val)}
                                            label={t('max-installments')}
                                            width={'4rem'}
                                            numericOnly={true}
                                            min={1}
                                            max={24}
                                            error={getPartialError(indexImmediate, 'max_installments')}
                                            containerProps={{mb: 0}}
                                            errorHeight={'unset'}
                                            labelProps={{style:{fontFamily: 'Ploni'}}}
                                        />}
                                </HStack>
                                {taxes &&
                                    <VStack space={'0.2rem'} w={'12rem'}>
                                        <AppText fontSize={GlobalStyleAttributes.fonts.infoText}>{t('taxes')}</AppText>
                                        <MultiSingleMenuWithSearch options={taxes} onChange={(val) => setFieldValue(`partialOptions[${indexImmediate}].taxes`, val)} placeholder={t('taxSelectorLabel')} defaultValue={values.partialOptions[indexImmediate].taxes}/>
                                    </VStack>
                                }
                            </VStack>
                        </VStack>
                    }
                    <VStack space={'0.5rem'}>
                        <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('name')}</AppText>
                        <VStack space={'0.2rem'}>
                            <AppText fontSize={GlobalStyleAttributes.fonts.heading3}>{t('name-partial')}</AppText>
                            <SimpleInput value={name}
                                         onChangeCallback={setName}
                                         status={getPartialError(indexHOK, 'name') || getPartialError(indexImmediate, 'name') ? 'error' : null}
                            />
                            <AppText fontSize={GlobalStyleAttributes.fonts.infoText} color={Colors.error} mediumFont>{getPartialError(indexHOK, 'name') ?? getPartialError(indexImmediate, 'name') ?? ''}</AppText>
                        </VStack>
                    </VStack>
                </VStack>
            </FoldableSettingSection>
        )
    }

    const getFormConnectionSection = () => {
        return (
            <FoldableSettingSection header={t('hug-form-connection')}
                                    isOpen={openedSection === HUG_FORM_SECTION}
                                    onToggle={() => onSectionToggle(HUG_FORM_SECTION)}
                                    summary={t(`hug-form-summary-${values.form_id ? 'connected' : 'disconnected'}${isEdit ? '-edit' : ''}`)}
                                    infoTooltip={t('hug-form-tooltip')}
            >
                <VStack space={'0.2rem'} flex={1}>
                    <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('form-name')}</AppText>
                    <AdvanceSelect options={formOptions}
                                   initValue={values.form_id}
                                   onChange={(val) => setFieldValue('form_id', val?.id ?? null)}
                                   placeholder={t('hug-form-placeholder')}
                                   isClearable={true}/>
                </VStack>
            </FoldableSettingSection>
        )
    }

    return (
        <VStack flex={1} justifyContent={'space-between'} space={'1rem'}>
            <VStack space={'1rem'}>
            {values.paymentOptions && values.paymentOptions.length > 0 &&
                <>
                    {showAddonsOption && getAddonsSection()}
                    {showTrialOption && renderTrialSection()}
                    {showHugFormOption && getFormConnectionSection()}
                </>
            }
            {values.partialOptions && values.partialOptions.length > 0 &&
                [...Array(seriesEvents.length - 1).keys()].map(i => renderPartialSection(i + 1))
            }
            </VStack>
        </VStack>
    );
};

export default AdvancedSeriesSettings;